
import { useState, useEffect } from 'react';

// Update to support any string variant, not just 'A' or 'B'
type Variant = string;

interface UseABTestOptions {
  testName: string;
  variants: Variant[];
  defaultVariant?: Variant;
  weights?: number[]; // Optional weights for non-equal distribution
}

export function useABTest({ 
  testName, 
  variants, 
  defaultVariant, 
  weights 
}: UseABTestOptions) {
  const [variant, setVariant] = useState<Variant>(defaultVariant || variants[0]);
  
  useEffect(() => {
    // Check if variant is already stored in localStorage
    const storedVariant = localStorage.getItem(`abtest_${testName}`);
    
    if (storedVariant && variants.includes(storedVariant)) {
      setVariant(storedVariant);
    } else {
      // Assign a random variant based on weights or equal distribution
      let newVariant: Variant;
      
      if (weights && weights.length === variants.length) {
        // Weighted random selection
        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
        const random = Math.random() * totalWeight;
        
        let cumulativeWeight = 0;
        for (let i = 0; i < weights.length; i++) {
          cumulativeWeight += weights[i];
          if (random < cumulativeWeight) {
            newVariant = variants[i];
            break;
          }
        }
      } else {
        // Equal distribution
        const randomIndex = Math.floor(Math.random() * variants.length);
        newVariant = variants[randomIndex];
      }
      
      setVariant(newVariant!);
      
      // Store the variant for consistent user experience
      localStorage.setItem(`abtest_${testName}`, newVariant!);
      
      // Log for analytics
      console.log(`A/B Test "${testName}": User assigned to variant ${newVariant}`);
      
      // Optional: Track event in analytics
      try {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'ab_test_assignment', {
            'test_name': testName,
            'variant': newVariant
          });
        }
      } catch (e) {
        console.error('Failed to track A/B test assignment:', e);
      }
    }
  }, [testName, variants, weights]);
  
  return {
    variant,
    isVariant: (v: Variant) => variant === v,
  };
}
